// Running project (RF-UI-09..13): breadcrumb, name + state, Start/Pause/Resume/Drain/Stop
// via the execution API with expected_version and exact 409 causes, the publication status and primary action (D31:
// "Open app" once published, "Publish" for an admin once every task is done, else "Open preview") and tabs,
// including memory and incidents/pull requests.
// The board is loaded once here and shared by Overview and Kanban; realtime events for
// this project refresh it in place (no remount, no focus or scroll jumps).
import { useCallback, useEffect, useMemo } from "react";
import { Link, NavLink, useParams } from "react-router";
import type { Board, EventEnvelope } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { getBoard, getProject, getProjectDetail } from "../../api/endpoints.ts";
import { isApiError } from "../../api/client.ts";
import { useAsync, useDebouncedCallback } from "../../lib/useAsync.ts";
import { AGGREGATE_VIEW, TASK_STATUS_VIEW } from "../../lib/status.ts";
import { useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { BrowserTab } from "../../features/browser/BrowserTab.tsx";
import { ProjectMemory } from "../../features/memory/ProjectMemory.tsx";
import { IncidentsList, PullRequestList } from "../../features/integrations/SignalLists.tsx";
import { StatusPill } from "../../components/Status.tsx";
import { BlockAttention } from "../../features/blocks/BlockPanel.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ChevronRightIcon } from "../../components/icons.tsx";
import { getPreview } from "../../api/endpoints-wave3.ts";
import { useOrgRole } from "../../features/config/shared.tsx";
import { StaleDataNotice } from "../../shell/ConnectionStatus.tsx";
import { OverviewTab } from "./OverviewTab.tsx";
import { KanbanTab } from "./KanbanTab.tsx";
import { PrdsTab } from "./PrdsTab.tsx";
import { ProjectSettingsTab } from "./ProjectSettingsTab.tsx";
import { ExecutionControls } from "./ExecutionControls.tsx";
import { RunsList } from "../../features/runs/RunsList.tsx";
import { ReportsCard } from "../../features/runs/ReportsCard.tsx";
import { LaunchCard, allTasksDone } from "../../features/microsystem/LaunchCard.tsx";
import { ProjectHeaderActions, PublicationStatus, useProjectPublication } from "../../features/microsystem/Publication.tsx";
import { AgentRunsTable } from "../../features/microsystem/AgentRunsTable.tsx";

import { ProjectFiles } from "../../features/project-files/ProjectFiles.tsx";
import { UnknownOperationsPanel } from "../../features/operations/UnknownOperationsPanel.tsx";

const TABS = ["overview", "kanban", "prds", "code", "files", "runs", "preview", "signals", "memory", "settings"] as const;
type ProjectTab = (typeof TABS)[number];

export function ProjectPage() {
  const { t } = useI18n();
  const { announce } = useToast();
  const params = useParams();
  const projectId = params["projectId"] ?? "";
  const tab: ProjectTab = (TABS as readonly string[]).includes(params["tab"] ?? "") ? (params["tab"] as ProjectTab) : "overview";
  const project = useAsync(() => getProject(projectId), [projectId]);
  const board = useAsync((signal) => getBoard(projectId, signal), [projectId]);
  // The readiness detail (fix round 1, minor): fetched once here and shared by the header badge
  // (ExecutionControls) and the Settings readiness card, instead of each fetching it separately —
  // one realtime subscription keeps both in sync, including after the header's own "Validate now".
  const detail = useAsync((s) => getProjectDetail(projectId, s), [projectId, project.data?.version]);
  const refreshBoard = useDebouncedCallback(board.reload, 300);
  const refreshProject = useDebouncedCallback(project.reload, 300);
  const refreshDetail = useDebouncedCallback(detail.reload, 500);
  // D31: the publication (latest publish, polled while in flight) and the preview targets drive the header's status and
  // primary action; both are shared with the Overview preview card and the Preview & tests tab.
  const publication = useProjectPublication(projectId);
  const previewTargets = useAsync((s) => getPreview(projectId, s), [projectId]);
  const isOrgAdmin = useOrgRole().atLeast("admin");
  const tasksDone = useMemo(() => allTasksDone(board.data), [board.data]);
  const refreshPublication = useDebouncedCallback(publication.reload, 300);
  const refreshPreviewTargets = useDebouncedCallback(previewTargets.reload, 300);
  const publishState = publication.publish?.state;
  // A succeeded publish registers the app as a preview target: read the targets again once it lands.
  useEffect(() => {
    if (publishState === "succeeded") previewTargets.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishState]);

  const findTaskNumber = useCallback(
    (b: Board | undefined, taskId: string) => {
      if (!b) return null;
      for (const c of [...b.columns, ...b.side_lane]) {
        const found = c.tasks.find((x) => x.id === taskId);
        if (found) return found.number;
      }
      return null;
    },
    [],
  );

  useRealtimeEvents((e: EventEnvelope) => {
    if (e.type.startsWith("task.") || e.type.startsWith("prd.") || e.type === "requirement.extracted" || e.type.startsWith("run.") || e.type.startsWith("model.") || e.type.startsWith("quota.wait.")) refreshBoard();
    // The header state (aggregate: running / needs attention / blocked / completed) is derived from
    // task facts, so task transitions refresh it too — otherwise it stays "Not started" while tasks run.
    if (e.type.startsWith("project.") || e.type === "task.transitioned" || e.type === "task.blocked" || e.type === "task.completed" || e.type === "task.created") refreshProject();
    if (e.type === "routing.config.activated" || e.type === "project.validation.completed" || e.type === "agent.profile.activated") refreshDetail();
    if (e.type === "project.publish.changed") refreshPublication();
    if (e.type === "preview.changed") refreshPreviewTargets();
    if (e.type === "task.transitioned") {
      const to = typeof e.payload["to"] === "string" ? (e.payload["to"] as keyof typeof TASK_STATUS_VIEW) : null;
      const number = typeof e.payload["number"] === "number" ? e.payload["number"] : findTaskNumber(board.data, e.aggregate_id);
      if (to && TASK_STATUS_VIEW[to] && number !== null) announce(t("realtime.taskMoved", { number, status: t(TASK_STATUS_VIEW[to].label) }));
    }
  }, projectId);

  const p = project.data;

  if (project.loading) return <LoadingBlock label={t("common.loading")} lines={5} />;
  if (project.error || !p)
    return (
      <div className="page">
        <ErrorBanner error={project.error} onRetry={project.reload} title={isApiError(project.error) && project.error.status === 404 ? t("project.notFound") : t("project.loadFailed")} />
      </div>
    );

  const tabLabels: Record<ProjectTab, MessageKey> = {
    overview: "project.tab.overview",
    kanban: "project.tab.kanban",
    prds: "project.tab.prds",
    code: "project.tab.code",
    files: "project.tab.files",
    runs: "project.tab.runs",
    preview: "project.tab.preview",
    signals: "project.tab.signals",
    memory: "project.tab.memory",
    settings: "project.tab.settings",
  };

  return (
    <div className="page project-page">
      <nav aria-label={t("project.breadcrumb")} className="breadcrumb">
        <ol>
          <li>
            <Link to="/projects">{t("nav.projects")}</Link>
          </li>
          <li aria-hidden="true">
            <ChevronRightIcon size={14} />
          </li>
          <li>
            <span aria-current="page">{p.name}</span>
          </li>
        </ol>
      </nav>
      <header className="project-header">
        <div className="stack" style={{ gap: 6, minWidth: 0 }}>
          <div className="row row--wrap" style={{ gap: 12 }}>
            <h1 className="page-title">{p.name}</h1>
            <StatusPill view={AGGREGATE_VIEW[p.aggregate_state]} />
            <PublicationStatus publish={publication.publish} />
          </div>
          {p.description ? <p className="page-subtitle user-content">{p.description}</p> : null}
          {p.attention_reason && !p.attention ? <p className="project-card-attention">{p.attention_reason}</p> : null}
        </div>
        <ProjectHeaderActions
          projectId={p.id}
          publication={publication}
          previewTargets={previewTargets.error ? [] : previewTargets.data?.items}
          isOrgAdmin={isOrgAdmin}
          tasksDone={tasksDone}
          controls={<ExecutionControls project={p} onChanged={project.reload} detail={detail} />}
        />
      </header>
      {/* D37: the task behind the attention reason, translated, with what waits for it and what to do. */}
      <BlockAttention project={p} />

      <nav className="tabs project-tabs" aria-label={t("project.sections")}>
        {TABS.map((id) => (
          <NavLink key={id} to={id === "overview" ? `/projects/${p.id}` : `/projects/${p.id}/${id}`} end className="tab">
            {t(tabLabels[id])}
          </NavLink>
        ))}
      </nav>

      <StaleDataNotice />

      <div className="project-tab-content">
        {tab === "overview" ? <OverviewTab project={p} board={board} publication={{ publication, isOrgAdmin, tasksDone }} /> : null}
        {tab === "kanban" ? (
          <div className="stack">
            <LaunchCard project={p} board={board} onProjectChanged={project.reload} />
            <KanbanTab project={p} board={board} onProjectChanged={project.reload} />
          </div>
        ) : null}
        {tab === "prds" ? <PrdsTab project={p} onChanged={board.reload} /> : null}
        {tab === "settings" ? <ProjectSettingsTab project={p} onSaved={project.reload} detail={detail} /> : null}
        {tab === "code" || tab === "files" ? <ProjectFiles key={p.id} projectId={p.id} /> : null}
        {tab === "runs" ? (
          <div className="stack">
            <RunsList projectId={p.id} />
            <AgentRunsTable projectId={p.id} />
            <ReportsCard projectId={p.id} />
          </div>
        ) : null}
        {tab === "preview" ? <BrowserTab projectId={p.id} publication={{ publication, isOrgAdmin, tasksDone }} /> : null}
        {tab === "signals" ? (
          <div className="section-stack">
            <UnknownOperationsPanel projectId={p.id} canResolve={isOrgAdmin} />
            <IncidentsList projectId={p.id} />
            <PullRequestList projectId={p.id} />
          </div>
        ) : null}
        {tab === "memory" ? <ProjectMemory projectId={p.id} /> : null}
      </div>
    </div>
  );
}
